/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { AxiosError } from "axios";
import { FC, JSX } from "react";
import { useSelector } from "react-redux";

import { useAxiosRequest } from "../../../../../hooks/useAxiosRequest";
import apiClient from "../../../../../services/api-client";
import { getStringFromArray } from "../../../../../util/formatting";
import {
  selectContactInfo,
  selectIsThisTfo,
  selectLoginEmail,
  selectUserContactDetails,
} from "../../../../azure-b2c/storage";

import DeleteAccountConfirmation from "./DeleteAccountConfirmation";
import DeleteAccountError from "./DeleteAccountError";
import DeleteAccountLoading from "./DeleteAccountLoading";
import DeleteAccountSuccess from "./DeleteAccountSuccess";

export interface IDeleteAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DeleteAccountModal: FC<IDeleteAccountModalProps> = ({ open, onOpenChange }): JSX.Element => {
  const accountEmail = useSelector(selectLoginEmail) ?? undefined;
  const relationshipOwner = useSelector(selectContactInfo);
  const contactDetail = useSelector(selectUserContactDetails);
  const contactName = getStringFromArray([contactDetail?.firstName, contactDetail?.lastName]);
  const relationshipType = useSelector(selectIsThisTfo) ? "TFO" : undefined;

  const {
    triggerRequest: triggerOffboardingRequest,
    isRequesting,
    successResponse,
    errorResponse,
  } = useAxiosRequest({
    request: () => apiClient.post<void, AxiosError>("/offboarding"),
  });

  const showConfirmation = open && !isRequesting && !successResponse && !errorResponse;
  const showLoading = open && isRequesting;
  const showError = open && !isRequesting && !!errorResponse;
  const showSuccess = open && !isRequesting && !!successResponse;

  return (
    <>
      <DeleteAccountConfirmation
        open={showConfirmation}
        onOpenChange={onOpenChange}
        onDeleteAccountClick={triggerOffboardingRequest}
        contactName={contactName}
        accountEmail={accountEmail}
      />

      <DeleteAccountLoading open={showLoading} />

      <DeleteAccountError
        open={showError}
        onRetry={triggerOffboardingRequest}
        onClose={() => onOpenChange(false)}
      />

      <DeleteAccountSuccess
        open={showSuccess}
        onOpenChange={onOpenChange}
        relationshipOwner={relationshipOwner}
        relationshipType={relationshipType}
      />
    </>
  );
};

export default DeleteAccountModal;
